export const BASE_URL = process.env.REACT_APP_API_URL || "https://server-e-fx6s.onrender.com";
export const MAIL_URL = process.env.REACT_MAIL || "https://server-e.vercel.app";
// export const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:7000";
/**
 * Converts a relative image path from the backend into a full, absolute URL.
 * @param {string} path - The relative path of the image (e.g., 'uploads/products/image.jpg').
 * @returns {string} The full image URL or the original path if it's already a full URL.
 */
export const getFullImageUrl = (path) => {
  // Normalize non-string inputs (objects/arrays) to extract a usable URL/path
  if (!path) return path;

  // If the caller passed an object like { url } or { path } or an image object, try to extract string
  if (typeof path === 'object') {
    // Common fields that may contain the URL
    const candidates = [path.url, path.path, path.image, path.src];
    for (const c of candidates) {
      if (typeof c === 'string' && c) {
        path = c;
        break;
      }
    }
    // If still an object to stringify fallback
    if (typeof path !== 'string') {
      return '';
    }
  }

  // Now path is a string — guard non-string earlier
  if (typeof path !== 'string') return '';

  // If the path is already a full URL
  if (path.startsWith('http')) return path;

  // If the path starts with a slash, remove it to prevent double slashes.
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${BASE_URL}/${cleanPath}`;
};

/**
 * A centralized function to make API calls.
 * It automatically adds the auth token and handles JSON conversion.
 * @param {string} path - The API endpoint path (e.g., '/api/users/login').
 * @param {object} options - Configuration for the fetch call (method, body, etc.).
 * @returns {Promise<any>} The JSON response from the server.
 */
export async function apiFetch(path, { method = "GET", body = null, headers = {}, credentials = false, baseUrl = BASE_URL } = {}) {
  const token = localStorage.getItem("token");
  const finalHeaders = { ...headers };

  const isForm = body instanceof FormData;
  if (body && !isForm && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
    body = JSON.stringify(body);
  }

  if (token) {
    finalHeaders["token"] = token;
  }

  const opts = { method, headers: finalHeaders };
  if (body) opts.body = body;
  if (credentials) opts.credentials = "include";

  const res = await fetch(`${baseUrl}${path}`, opts);

  const contentType = res.headers.get("content-type") || "";
  let data = null;

  if (contentType.includes("application/json")) {
    try {
      data = await res.json();
    } catch {
      data = { message: res.statusText };
    }
  } else {
    data = await res.text();
  }

  if (typeof data !== 'object' || data === null) {
    data = { message: typeof data === 'string' ? data : res.statusText };
  }

  if (res.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    const msg = data.message || "Authorization token required. Please log in again.";
    throw new Error(msg);
  }

  if (method === 'DELETE' && res.status === 404) {
    return {
      success: true,
      message: data.message || "Resource deleted successfully",
      data: null
    };
  }

  if (typeof data === 'string') {
    const lowerData = data.toLowerCase();
    if (lowerData.includes('success') || lowerData.includes('deleted') || lowerData.includes('created') || lowerData.includes('updated')) {
      return {
        success: true,
        message: data,
        data: null
      };
    }
  }

  if (!res.ok) {
    const msg = data.message || data.error || res.statusText;
    throw new Error(msg || 'Request failed');
  }

  if (data.success === undefined) {
    data.success = true;
  }

  return data;
}

// --- Product API Functions ---
export async function getAllProducts(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/products/all${query ? `?${query}` : ""}`);
}

export async function getProductById(productId) {
  return apiFetch(`/api/products/byId/${productId}`);
}

// --- Order API Functions ---
export async function createOrder(orderData) {
  return apiFetch("/api/orders/create", { method: "POST", body: orderData });
}

export async function getOrderById(orderId) {
  return apiFetch(`/api/orders/byId/${orderId}`);
}

export async function updateOrder(orderId, updateData) {
  return apiFetch(`/api/orders/update/${orderId}`, { method: "PUT", body: updateData });
}

export async function deleteOrder(orderId) {
  return apiFetch(`/api/orders/delete/${orderId}`, { method: "DELETE" });
}

export async function getAllOrders() {
  return apiFetch("/api/orders/all");
}

// --- Address API Functions ---
export async function createAddress(addressData) {
  return apiFetch("/api/addresses/create", { method: "POST", body: addressData });
}

export async function getUserAddresses(userId) {
  return apiFetch(`/api/addresses/user/${userId}`);
}

export async function updateAddress(addressId, addressData) {
  return apiFetch(`/api/addresses/update/${addressId}`, { method: "PUT", body: addressData });
}

export async function deleteAddress(addressId) {
  return apiFetch(`/api/addresses/delete/${addressId}`, { method: "DELETE" });
}

export async function getAllAddresses(params = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch(`/api/addresses/all${query ? `?${query}` : ""}`);
}

// --- User API Functions ---
export async function registerUser(userData) {
  return apiFetch("/api/users/register", { method: "POST", body: userData });
}

export async function loginUser(credentials) {
  return apiFetch("/api/users/login", { method: "POST", body: credentials });
}

export async function updateUser(userId, userData) {
  return apiFetch(`/api/users/update/${userId}`, { method: "PUT", body: userData });
}

export async function uploadProfileImage(formData) {
  return apiFetch("/api/users/upload-profile", { method: "POST", body: formData });
}

export async function getCurrentUser() {
  return apiFetch("/api/users/me");
}

/**
 * Fetch city/state information for an Indian PIN code using public API
 * Returns { district, state, name } on success or throws an Error
 */
export async function fetchPincodeIndia(pincode) {
  if (!/^[0-9]{6}$/.test(String(pincode))) {
    throw new Error('Invalid PIN code');
  }
  const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  const data = await res.json();
  if (!Array.isArray(data) || !data[0]) throw new Error('Pincode lookup failed');
  const first = data[0];
  if (first.Status !== 'Success' || !first.PostOffice || first.PostOffice.length === 0) {
    throw new Error('Pincode not found');
  }
  const po = first.PostOffice[0];
  return {
    district: po.District || '',
    state: po.State || '',
    name: po.Name || ''
  };
}